/**
 * IDP-1300: public app expiry dates — API access and display helpers.
 *
 * Every public app expires a set number of days (default 30) after its last
 * deploy, redeploy or Extend. Expired apps are deleted with all their
 * resources; owners are emailed 7 days and 1 day before.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

export interface PublicAppExpiryInfo {
  appName: string;
  /** ISO timestamp. */
  expiresAt: string;
  /** Whole days left, rounded up (0 or less = expired). */
  daysLeft: number;
}

export type ExpiryTone = 'ok' | 'warning' | 'danger';

/** Reminder emails start at 7 days, so that is when the UI turns amber. */
export const EXPIRY_WARNING_DAYS = 7;

export function describeExpiry(daysLeft: number): {
  label: string;
  tone: ExpiryTone;
} {
  if (daysLeft <= 0) return { label: 'Expired', tone: 'danger' };
  if (daysLeft === 1) return { label: 'Expires in 1 day', tone: 'danger' };
  return {
    label: `Expires in ${daysLeft} days`,
    tone: daysLeft <= EXPIRY_WARNING_DAYS ? 'warning' : 'ok',
  };
}

export const formatExpiryDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const readError = async (response: Response): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as { error?: string };
  return body.error ?? `Server error ${response.status}`;
};

/**
 * Loads the expiry dates of all public apps (one request for the whole card
 * grid) and exposes Extend, which updates the local copy on success.
 */
export function usePublicAppExpiry() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [items, setItems] = useState<Map<string, PublicAppExpiryInfo>>(
    new Map(),
  );
  const [expiryDays, setExpiryDays] = useState<number | undefined>();
  const [maxExpiryDays, setMaxExpiryDays] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
        const response = await fetchApi.fetch(`${baseUrl}/public-apps/expiry`);
        if (!response.ok) throw new Error(await readError(response));
        const body = (await response.json()) as {
          expiryDays?: number;
          maxExpiryDays?: number;
          items?: PublicAppExpiryInfo[];
        };
        if (cancelled) return;
        setItems(new Map((body.items ?? []).map(i => [i.appName, i])));
        setExpiryDays(body.expiryDays);
        setMaxExpiryDays(body.maxExpiryDays);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const extend = useCallback(
    async (appName: string): Promise<PublicAppExpiryInfo> => {
      const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/public-apps/${encodeURIComponent(appName)}/extend-expiry`,
        { method: 'POST' },
      );
      if (!response.ok) throw new Error(await readError(response));
      const info = (await response.json()) as PublicAppExpiryInfo;
      setItems(prev => new Map(prev).set(appName, info));
      return info;
    },
    [discoveryApi, fetchApi],
  );

  return { items, expiryDays, maxExpiryDays, loading, error, extend };
}
